import React from "react";
import { ShoppingCart } from "lucide-react";

// Bloco cinza pulsante usado no esqueleto
const Bar = ({ className = "" }) => (
  <div className={`bg-gray-200 rounded-md animate-pulse ${className}`} />
);

// Card do esqueleto com o mesmo visual das seções reais do checkout
const SkeletonCard = ({ children }) => (
  <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 space-y-4">
    {children}
  </div>
);

/**
 * Tela de carregamento do /checkout.
 * Reproduz o layout da página (cabeçalho + seções + resumo) em esqueleto enquanto
 * as configurações da loja (delivery, pagamento online) são consultadas, para que
 * opções indisponíveis já cheguem ocultas, sem "pular" na frente do cliente.
 */
export default function CheckoutLoadingScreen({
  message = "Preparando seu pedido...",
}) {
  return (
    <div
      className="min-h-screen bg-gray-50"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {/* Header (mesma altura e estrutura do header real) */}
      <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 sm:gap-4">
            <Bar className="w-5 h-5 sm:w-16 flex-shrink-0" />
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-brand-primary flex-shrink-0" />
              <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">
                Finalizar Pedido
              </h1>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <Bar className="h-3 w-10" />
              <Bar className="h-5 sm:h-6 w-20 sm:w-24" />
            </div>
          </div>
        </div>
      </div>

      {/* Indicador de progresso */}
      <div className="h-1 w-full bg-brand-100 overflow-hidden">
        <div className="h-full w-1/3 bg-brand-600 rounded-full animate-[checkout-progress_1.2s_ease-in-out_infinite]" />
      </div>
      <style>{`
        @keyframes checkout-progress {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center gap-2 mb-6 text-sm text-gray-600">
          <span className="w-4 h-4 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
          <span>{message}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Coluna Principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Entrega */}
            <SkeletonCard>
              <Bar className="h-6 w-40" />
              <Bar className="h-4 w-32" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Bar className="h-14" />
                <Bar className="h-14" />
              </div>
              <Bar className="h-11" />
              <Bar className="h-11" />
            </SkeletonCard>

            {/* Cupom */}
            <SkeletonCard>
              <Bar className="h-6 w-32" />
              <Bar className="h-11" />
            </SkeletonCard>

            {/* Pagamento */}
            <SkeletonCard>
              <Bar className="h-6 w-44" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Bar className="h-14" />
                <Bar className="h-14" />
                <Bar className="h-14" />
                <Bar className="h-14" />
              </div>
            </SkeletonCard>
          </div>

          {/* Resumo */}
          <div className="lg:col-span-1">
            <SkeletonCard>
              <Bar className="h-6 w-36" />
              <div className="space-y-3">
                <Bar className="h-4" />
                <Bar className="h-4 w-5/6" />
                <Bar className="h-4 w-4/6" />
              </div>
              <div className="border-t pt-4 space-y-3">
                <Bar className="h-4 w-1/2" />
                <Bar className="h-6 w-2/3" />
              </div>
              <Bar className="h-12 rounded-lg" />
            </SkeletonCard>
          </div>
        </div>
      </div>
    </div>
  );
}
